import { firstRealPrice } from 'utils/price';
import { Activity } from 'types/interfaces/activity';
import { SidebarTradeCard } from 'screens/Public/UserProfile/context/ProfileTradeSidebarContext';
import { finishFields, haveFinishFromRow } from 'utils/finishes';
import { stagedPrintingFromResolved } from 'utils/wantListHelpers';

/**
 * Map a want_added/available activity item onto the SidebarTradeCard shape ComposeTradeModal
 * pre-stages. Mirrors Trade Alerts' `alertToStagedCard`: the wanter's acceptance criteria
 * (present on want/available items) flow through so the modal's version flyout shows the real
 * set/finish/condition/language options; when absent the modal falls back to the single
 * displayed printing. `wantEntryId` is the wanter's want-list entry id.
 *
 * The language and printing come from the item's defaultLanguage / defaultLanguageCard, the
 * same rule the want-list row uses (Asana 1219287979346676); REST and socket-pushed items
 * carry the same fields. Returns null when the item resolves no sendable version (the tile
 * disables Trade for that) or carries no resolution at all (Compose opens with nothing staged,
 * and the wanter's row in its list stages from the want-list response).
 */
export const activityToStagedCard = (activity: Activity): SidebarTradeCard | null => {
  const { card } = activity;
  const staged = stagedPrintingFromResolved(activity, card.id);
  if (!staged) return null;
  return {
    id: `activity-${activity.id}`,
    wantListId: activity.wantEntryId ?? 0,
    cardId: staged.cardId,
    cardName: card.name,
    set: card.setCode,
    setCode: card.setCode,
    condition: 'NEAR_MINT',
    language: staged.language,
    ...finishFields(haveFinishFromRow(card)),
    imageUrl: staged.imageUrl ?? card.imageUrl,
    // The staged tuple's price; null when unpriced, and the staged row re-prices itself
    pointValue: firstRealPrice(activity.pointValue),
    scryfallId: staged.scryfallId ?? card.scryfallId,
    acceptFoil: card.acceptFoil ?? true,
    acceptNonFoil: card.acceptNonFoil ?? true,
    availableInFoil: card.availableInFoil ?? card.foil,
    availableInNonFoil: card.availableInNonFoil ?? !card.foil,
    acceptableSetDetails: card.acceptableSetDetails,
    acceptableConditions: card.acceptableConditions,
    acceptableLanguages: card.acceptableLanguages,
    availableLanguages: card.availableLanguages,
    maxQuantity: 1,
  };
};

/** The wanter resolves no sendable version of this want: No price, and Trade is disabled. */
export const isUnsendableWant = (activity: Activity): boolean => activity.defaultLanguage === null;
